import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { TIER_PRICING } from '@/lib/pricing-utils';

export interface PricingTier {
  name: 'Starter' | 'Growth' | 'Pro' | 'Enterprise';
  maxUnits: number;
  price: number;
  features: {
    auditTrail: boolean;
    pdfExport: boolean;
    emailSupport: boolean;
    multiUnitDashboard: boolean;
    roleBasedAccess: boolean;
    advancedReports: boolean;
    prioritySupport: boolean;
    customLawPacks: boolean;
    customTemplates: boolean;
    auditHistory: boolean;
    crystalTemplatesBasic: boolean;
    crystalTemplatesFull: boolean;
  };
}

const PRICING_TIERS: Record<string, PricingTier> = {
  'Starter': {
    name: 'Starter',
    maxUnits: 999, // No unit limit - any customer can choose
    price: TIER_PRICING.Starter.unitPrice, // Per unit price
    features: {
      auditTrail: true, // HACCP logs, Corrective actions tracking
      pdfExport: true, // Audit export (PDF/Excel)
      emailSupport: true, // Email support (72h response)
      multiUnitDashboard: false,
      roleBasedAccess: false,
      advancedReports: false,
      prioritySupport: false,
      customLawPacks: false,
      customTemplates: false,
      auditHistory: false,
      crystalTemplatesBasic: true, // 4 basic Crystal templates (10 items each)
      crystalTemplatesFull: false,
    }
  },
  'Growth': {
    name: 'Growth',
    maxUnits: 999, // No unit limit - any customer can choose
    price: TIER_PRICING.Growth.unitPrice, // Per unit price
    features: {
      auditTrail: true, // Includes all Starter features
      pdfExport: true,
      emailSupport: true,
      multiUnitDashboard: true, // Multi-unit dashboard
      roleBasedAccess: true, // Role-based access control
      advancedReports: true, // Advanced reporting & analytics
      prioritySupport: true, // Priority email support (48h response)
      customLawPacks: false,
      customTemplates: false,
      auditHistory: false,
      crystalTemplatesBasic: true, // 4 basic Crystal templates (10 items each)
      crystalTemplatesFull: false,
    }
  },
  'Pro': {
    name: 'Pro',
    maxUnits: 999, // No unit limit - any customer can choose
    price: TIER_PRICING.Pro.unitPrice, // Per unit price
    features: {
      auditTrail: true, // Includes all Growth features
      pdfExport: true,
      emailSupport: true,
      multiUnitDashboard: true,
      roleBasedAccess: true,
      advancedReports: true,
      prioritySupport: true,
      customLawPacks: true, // Regional compliance presets
      customTemplates: true, // Custom checklists & workflows
      auditHistory: true, // Audit history with export filters
      crystalTemplatesBasic: true, // 4 basic Crystal templates (10 items each)
      crystalTemplatesFull: true, // Full Crystal template library (15+ items each)
    }
  }
};

export const usePricingAccess = () => {
  const { user } = useAuth();

  // Development mode based on environment (safer than hardcoded email)
  const isDeveloper = import.meta.env.DEV;

  // Get current tenant and unit count
  const { data: tenantData, isLoading: tenantLoading } = useQuery({
    queryKey: ['tenant-with-details', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('tenant_id, tenants!inner(*)')
        .eq('user_id', user.id)
        .single();

      return userRoles?.tenants || null;
    },
    enabled: !!user?.id,
  });

  const { data: unitCount, isLoading: unitsLoading } = useQuery({
    queryKey: ['tenant-unit-count', tenantData?.id],
    queryFn: async () => {
      if (!tenantData?.id) return 0;
      
      const { count } = await supabase
        .from('units')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tenantData.id);

      return count || 0;
    },
    enabled: !!tenantData?.id,
  });

  // Get selected tier from tenant data (defaults to Starter)
  const selectedTier = tenantData?.plan || 'starter';
  const currentTier = useMemo(() => {
    // Development mode: always return Pro tier in development environment
    if (isDeveloper) {
      return PRICING_TIERS.Pro;
    }
    
    // Convert plan name to match our tier names
    const tierName = selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1);
    return PRICING_TIERS[tierName] || PRICING_TIERS.Starter;
  }, [selectedTier, isDeveloper]);

  // Access control functions
  const hasFeature = useMemo(() => {
    return (feature: keyof PricingTier['features']) => {
      // Development mode: always return true in development environment
      if (isDeveloper) {
        return true;
      }
      
      return currentTier.features[feature];
    };
  }, [currentTier, isDeveloper]);

  const canAddUnits = useMemo(() => {
    return (additionalUnits: number = 1) => {
      // All tiers support unlimited units now
      return true;
    };
  }, []);

  const getUpgradeMessage = useMemo(() => {
    return (feature: keyof PricingTier['features']) => {
      if (hasFeature(feature)) return null;
      
      // Find the lowest tier that has this feature
      const requiredTier = Object.values(PRICING_TIERS).find(tier => 
        tier.features[feature]
      );

      return requiredTier 
        ? `Upgrade to ${requiredTier.name} plan to access this feature`
        : 'This feature is not available in any plan';
    };
  }, [hasFeature]);

  const isLoading = tenantLoading || unitsLoading;

  return {
    currentTier,
    unitCount: unitCount || 0,
    hasFeature,
    canAddUnits,
    getUpgradeMessage,
    isLoading,
    tenant: tenantData,
    pricingTiers: PRICING_TIERS,
  };
};